// src/store/slices/dashboardSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunks
export const fetchDashboardStats = createAsyncThunk('dashboard/fetchStats', async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get('/api/dashboard/stats');
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboard stats');
    }
});

export const fetchChartData = createAsyncThunk('dashboard/fetchChartData', async (chartType, { rejectWithValue }) => {
    try {
        const response = await axios.get(`/api/dashboard/charts/${chartType}`);
        return { chartType, data: response.data };
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch chart data');
    }
});

export const fetchRecentUsers = createAsyncThunk('dashboard/fetchRecentUsers', async (limit = 5, { rejectWithValue }) => {
    try {
        const response = await axios.get('/api/dashboard/recent-users', {
            params: { limit }
        });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch recent users');
    }
});

export const refreshDashboard = createAsyncThunk('dashboard/refresh', async (_, { dispatch }) => {
    await Promise.all([dispatch(fetchDashboardStats()), dispatch(fetchChartData('earnings')), dispatch(fetchRecentUsers())]);
    return true;
});

const initialState = {
    stats: {
        totalUsers: 0,
        activeUsers: 0,
        totalRevenue: 0,
        revenueGrowth: 0,
        newSignups: 0,
        signupGrowth: 0,
        conversionRate: 0,
        bounceRate: 0
    },

    charts: {
        earnings: {
            data: [],
            loading: false,
            error: null
        },
        users: {
            data: [],
            loading: false,
            error: null
        },
        performance: {
            data: [],
            loading: false,
            error: null
        }
    },

    recentUsers: {
        users: [],
        loading: false,
        error: null
    },

    refreshInterval: 300000, // 5 minutes
    lastRefresh: null,
    isAutoRefreshEnabled: true,

    loading: {
        stats: false,
        charts: false,
        recentUsers: false
    },

    error: null
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setRefreshInterval: (state, action) => {
            state.refreshInterval = action.payload;
        },
        toggleAutoRefresh: (state) => {
            state.isAutoRefreshEnabled = !state.isAutoRefreshEnabled;
        },
        setAutoRefreshEnabled: (state, action) => {
            state.isAutoRefreshEnabled = action.payload;
        },
        updateStats: (state, action) => {
            state.stats = { ...state.stats, ...action.payload };
            state.lastRefresh = Date.now();
        },
        clearError: (state) => {
            state.error = null;
        },
        resetDashboard: (state) => {
            Object.assign(state, initialState);
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch dashboard stats
            .addCase(fetchDashboardStats.pending, (state) => {
                state.loading.stats = true;
                state.error = null;
            })
            .addCase(fetchDashboardStats.fulfilled, (state, action) => {
                state.loading.stats = false;
                state.stats = { ...state.stats, ...action.payload };
                state.lastRefresh = Date.now();
                state.error = null;
            })
            .addCase(fetchDashboardStats.rejected, (state, action) => {
                state.loading.stats = false;
                state.error = action.payload;
            })

            // Fetch chart data
            .addCase(fetchChartData.pending, (state, action) => {
                const chartType = action.meta.arg;
                if (state.charts[chartType]) {
                    state.charts[chartType].loading = true;
                    state.charts[chartType].error = null;
                }
            })
            .addCase(fetchChartData.fulfilled, (state, action) => {
                const { chartType, data } = action.payload;
                if (state.charts[chartType]) {
                    state.charts[chartType].loading = false;
                    state.charts[chartType].data = data;
                    state.charts[chartType].error = null;
                }
            })
            .addCase(fetchChartData.rejected, (state, action) => {
                // Note: chartType is not available in rejected case without extra logic
                state.error = action.payload;
            })

            // Fetch recent users
            .addCase(fetchRecentUsers.pending, (state) => {
                state.loading.recentUsers = true;
                state.recentUsers.error = null;
            })
            .addCase(fetchRecentUsers.fulfilled, (state, action) => {
                state.loading.recentUsers = false;
                state.recentUsers.users = action.payload;
                state.recentUsers.error = null;
            })
            .addCase(fetchRecentUsers.rejected, (state, action) => {
                state.loading.recentUsers = false;
                state.recentUsers.error = action.payload;
            })

            // Refresh dashboard
            .addCase(refreshDashboard.pending, (state) => {
                // Set all loading states
                Object.keys(state.loading).forEach((key) => {
                    state.loading[key] = true;
                });
                state.error = null;
            })
            .addCase(refreshDashboard.fulfilled, (state) => {
                // Reset loading states
                Object.keys(state.loading).forEach((key) => {
                    state.loading[key] = false;
                });
                state.lastRefresh = Date.now();
            })
            .addCase(refreshDashboard.rejected, (state, action) => {
                // Reset loading states
                Object.keys(state.loading).forEach((key) => {
                    state.loading[key] = false;
                });
                state.error = action.payload;
            });
    }
});

export const { setRefreshInterval, toggleAutoRefresh, setAutoRefreshEnabled, updateStats, clearError, resetDashboard } =
    dashboardSlice.actions;

export default dashboardSlice.reducer;
